if (!customElements.get('footer-effect')) {
  customElements.define(
    'footer-effect',
    class FooterEffect extends HTMLElement {
      // Static property to track the currently active footer instance
      static activeInstance = null;
      constructor() {
        super();

        this.container = this.closest('footer');
        this.parentContainer = this.container?.parentElement;
        this.main = document.querySelector('[data-main]');
        this.parallaxElement = this.container?.querySelector('parallax-element');

        // Read settings from parallax-element or fallback to own attributes
        this.wavyEnabled = this.hasAttribute('data-parallax-wavy');
        this.roundedEnabled = this.getSettingFromParallax('data-parallax-rounded-corners');
        this.roundedLargeEnabled = this.getSettingFromParallax('data-parallax-rounded-corners-large');
        this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && this.getSettingFromParallax('data-parallax-disable-on-safari');

        this.resizeEvent = () => this.handleResize();
        this.currentWaveElement = null;
        this.targetSection = null;
        this.isActive = false;
      }

      /**
       * Get setting from parallax-element first, fallback to own attribute
       */
      getSettingFromParallax(attributeName) {
        // First try to read from parallax-element
        if (this.parallaxElement?.hasAttribute(attributeName)) {
          return this.parallaxElement.hasAttribute(attributeName);
        }

        // Fallback to own attribute
        return this.hasAttribute(attributeName);
      }

      connectedCallback() {
        requestIdleCallback(() => {
          this.evaluateActivation();
        });
      }

      /**
       * Determine if this footer should be the active one based on DOM position
       */
      evaluateActivation() {
        const allFooterEffects = this.getAllFooterEffects();
        const currentActive = FooterEffect.activeInstance;

        // Find this element's position in DOM order
        const thisIndex = allFooterEffects.indexOf(this);
        const currentActiveIndex = currentActive ? allFooterEffects.indexOf(currentActive) : -1;

        // This footer should be active if:
        // 1. No active footer exists, OR
        // 2. This footer comes before the current active footer in DOM order
        const shouldBeActive = !currentActive || (thisIndex !== -1 && thisIndex < currentActiveIndex);

        if (shouldBeActive) {
          this.becomeActive();
        }
      }

      /**
       * Get all footer-effect elements in DOM order
       */
      getAllFooterEffects() {
        return Array.from(document.querySelectorAll('footer-effect'));
      }

      /**
       * Become the active footer instance
       */
      becomeActive() {
        // Deactivate current active footer if it exists
        if (FooterEffect.activeInstance && FooterEffect.activeInstance !== this) {
          FooterEffect.activeInstance.becomePassive();
        }

        // Set this as the active instance
        FooterEffect.activeInstance = this;
        this.isActive = true;

        // Initialize parallax functionality
        this.init();

        document.addEventListener('theme:resize', this.resizeEvent);
      }

      /**
       * Become a passive footer instance (deactivate)
       */
      becomePassive() {
        if (!this.isActive) return;

        this.isActive = false;

        // Clean up parallax effects
        this.cleanup();

        // Remove event listeners
        document.removeEventListener('theme:resize', this.resizeEvent);

        // Clear static reference if this was the active instance
        if (FooterEffect.activeInstance === this) {
          FooterEffect.activeInstance = null;
        }
      }

      disconnectedCallback() {
        const wasActive = this.isActive;

        // Deactivate this footer
        this.becomePassive();

        // If this was the active footer, find the next one to activate
        if (wasActive) {
          this.handoverToNextFooter();
        }
      }

      /**
       * Find the next footer in DOM order and activate it
       */
      handoverToNextFooter() {
        // Small delay to ensure DOM has updated after this element is removed
        setTimeout(() => {
          const allFooterEffects = this.getAllFooterEffects();

          // Activate the first remaining footer-effect in DOM order
          if (allFooterEffects.length > 0 && !FooterEffect.activeInstance) {
            allFooterEffects[0].becomeActive();
          }
        }, 10);
      }

      init() {
        const stopParallax = this.shouldStopParallax();

        if (stopParallax) {
          this.removeFooterEffect();
          return;
        }

        this.applyFooterEffect();
      }

      shouldStopParallax() {
        const viewportHeight = Math.round(Math.max(document.documentElement.clientHeight, window.innerHeight || 0));
        const footerHeight = this.container?.clientHeight || 0;
        const waveHeight = this.wavyEnabled ? 50 : 0; // Half of wave height
        const isViewportSmaller = viewportHeight < footerHeight + waveHeight;

        return isViewportSmaller || window.innerWidth < theme.sizes.small || this.isSafari;
      }

      findPreviousSection() {
        if (!this.container) return null;

        let prevSection = this.container.closest('.shopify-section')?.previousElementSibling;

        if (prevSection && !prevSection.classList.contains('shopify-section') && !prevSection.classList.contains('main-content')) {
          // If previous section is not a shopify section/main, use main container
          prevSection = this.main;
        }

        return prevSection;
      }

      applyFooterEffect() {
        const prevSection = this.findPreviousSection();
        if (!prevSection) return;

        this.targetSection = prevSection;
        const bgColor = getComputedStyle(this.container).getPropertyValue('--bg');

        // Set footer background color on previous section
        prevSection.style.setProperty('--footer-bg', bgColor);

        // Reset parallax footer height
        this.parentContainer.style.setProperty('--footer-parallax-height', 'auto');

        if (this.wavyEnabled) {
          this.applyWavyEffect(prevSection);
        } else {
          this.applyNonWavyEffect(prevSection);
        }

        this.main.classList.add('main-content--parallax');
        this.parentContainer.classList.add('section-footer--parallax');
        this.parentContainer.style.setProperty('--footer-parallax-height', `${this.container.clientHeight}px`);
      }

      applyWavyEffect(prevSection) {
        // Remove old waves from previous section
        this.removeExistingWaves(prevSection);

        // Get wave element from footer
        const waveElement = this.container?.querySelector(':scope > [data-wave]');
        if (!waveElement) return;

        // Clone and append wave to previous section
        this.currentWaveElement = waveElement.cloneNode(true);
        prevSection.append(this.currentWaveElement);

        // Update classes
        prevSection.classList.add('wavy');
        this.container?.classList.remove('wavy');
      }

      applyNonWavyEffect(prevSection) {
        if (this.roundedEnabled) {
          // Apply rounded corners and shadow
          prevSection.classList.add('parallax-rounded-corners', 'parallax-shadow');

          if (this.roundedLargeEnabled) {
            document.body.classList.add('body--rounded-corners-large');
          }
        } else {
          // Just add shadow
          prevSection.classList.add('parallax-shadow');
        }
      }

      removeFooterEffect() {
        if (this.targetSection) {
          this.cleanupSection(this.targetSection);
        }

        // Also clean up any other sections that might have footer effects
        this.cleanupAllFooterEffects();

        // Reset footer to wavy if it should be
        if (this.wavyEnabled) {
          this.container?.classList.add('wavy');
        }

        this.main.classList.remove('main-content--parallax');
        this.parentContainer.classList.remove('section-footer--parallax');
        this.parentContainer.style.setProperty('--footer-parallax-height', 'auto');
      }

      cleanupSection(section) {
        if (!section) return;

        // Remove classes
        section.classList.remove('wavy', 'parallax-shadow', 'parallax-rounded-corners');

        // Remove waves
        this.removeExistingWaves(section);

        // Remove CSS properties
        section.style.removeProperty('--footer-bg');

        // Remove body class
        document.body.classList.remove('body--rounded-corners-large');
      }

      removeExistingWaves(section) {
        const existingWaves = section.querySelectorAll(':scope > [data-wave]');
        existingWaves.forEach((wave) => wave.remove());
      }

      cleanupAllFooterEffects() {
        // Remove wavy class from all sections except site footer
        const wavySections = document.querySelectorAll('.wavy:not(.site-footer)');
        wavySections.forEach((section) => {
          section.classList.remove('wavy');
          this.removeExistingWaves(section);
        });

        // Remove shadow and rounded classes from all sections except site footer
        const shadowSections = document.querySelectorAll('.parallax-shadow:not(.site-footer)');
        shadowSections.forEach((section) => {
          section.classList.remove('parallax-shadow', 'parallax-rounded-corners');
          this.removeExistingWaves(section);
          section.style.removeProperty('--footer-bg');
        });

        // Remove body class
        document.body.classList.remove('body--rounded-corners-large');
      }

      handleResize() {
        // Debounce resize handling
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          const shouldStop = this.shouldStopParallax();

          if (shouldStop) {
            this.removeFooterEffect();
          } else {
            // Reapply if not already applied
            if (!this.targetSection || !this.isEffectActive()) {
              this.applyFooterEffect();
            }
          }
        }, 150);
      }

      isEffectActive() {
        if (!this.targetSection) return false;

        if (this.wavyEnabled) {
          return this.targetSection.classList.contains('wavy') && this.targetSection.querySelector(':scope > [data-wave]');
        } else {
          return this.targetSection.classList.contains('parallax-shadow');
        }
      }

      cleanup() {
        this.removeFooterEffect();
        this.currentWaveElement = null;
        this.targetSection = null;

        clearTimeout(this.resizeTimer);
      }

      // Public API for manual refresh (useful for theme editor)
      refresh() {
        this.cleanup();
        this.init();
      }
    }
  );
}
